import type { SearchResult } from "@shared/types"

const MARK = "rounded bg-yellow-200 px-0.5 text-foreground dark:bg-yellow-500/40"

const BADGE = "rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

function highlight(text: string, query: string) {
  const tokens = query.toLowerCase().split(/\s+/).filter((t) => t.length > 1)
  if (!tokens.length) return text
  const re = new RegExp(`(${tokens.map(escapeRe).join("|")})`, "gi")
  return text.split(re).map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className={MARK}>
        {part}
      </mark>
    ) : (
      part
    ),
  )
}

export function ResultCard({ r, query, semantic }: { r: SearchResult; query: string; semantic?: boolean }) {
  const to = r.clusterId != null ? `/cases/${r.clusterId}` : undefined
  const name = r.caseName || r.caseNameFull || ""
  return (
    <a
      href={to}
      target="_blank"
      rel="noopener"
      title={to ? "Open case in a new tab" : undefined}
      className={`block space-y-2 rounded border p-3 transition-colors ${to ? "hover:bg-accent" : ""}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <span className="font-medium">{name ? highlight(name, query) : "—"}</span>
        <div className="flex shrink-0 items-center gap-2">
          {r.nameMatches != null && r.textMatches != null && (
            <span
              className={BADGE}
              title="Total times the search term appears in this case — case name plus opinion text"
            >
              {r.nameMatches + r.textMatches} matches
            </span>
          )}
          {/* The vector score is a cosine distance (0 = identical); show it as
              a similarity (1 − distance) so higher = closer. */}
          {r.score != null && (
            <span
              className={BADGE}
              title={
                semantic
                  ? "Cosine similarity between your query and this opinion — 1.00 means closest in meaning"
                  : "Turbopuffer BM25 relevance score — why this row ranked here"
              }
            >
              {semantic ? `similarity ${(1 - r.score).toFixed(3)}` : `BM25 ${r.score.toFixed(3)}`}
            </span>
          )}
        </div>
      </div>
      {r.caseName && r.caseNameFull && r.caseName !== r.caseNameFull && (
        <div className="text-xs text-muted-foreground">{highlight(r.caseNameFull, query)}</div>
      )}
      {r.snippet && (
        <p className="text-sm text-muted-foreground">
          {r.snippet.before}
          {r.snippet.match && <mark className={MARK}>{r.snippet.match}</mark>}
          {r.snippet.after}
        </p>
      )}
    </a>
  )
}

export function ResultList({
  results,
  query,
  semantic,
}: {
  results: SearchResult[]
  query: string
  semantic?: boolean
}) {
  return (
    <div className="space-y-3">
      {results.map((r) => (
        <ResultCard key={r.id} r={r} query={query} semantic={semantic} />
      ))}
    </div>
  )
}
